import type { Input } from 'mediabunny';

/**
 * Long clips (1hr) can have over 100M audio frames. Drawing the waveform never needs more
 * resolution than this.
 */
export const MAX_WAVEFORM_POINTS = 10000;

export const downsampleVolumes = (frames: Float32Array, numPoints: number) => {
    const volumes = new Float32Array(numPoints);
    const framesPerPoint = frames.length / numPoints;

    for (let i = 0; i < numPoints; i++) {
        const start = Math.floor(i * framesPerPoint);
        const end = Math.floor((i + 1) * framesPerPoint);
        let totalVolume = 0;
        for (let j = start; j < end; j++) {
            const val = frames[j];
            totalVolume += val < 0 ? -val : val;
        }
        volumes[i] = totalVolume / (end - start);
    }
    return volumes;
};

/**
 * Without retry limits mediabunny retries a failing request forever, so the waveform would never
 * fall back or finish loading.
 */
const MAX_FETCH_ATTEMPTS = 3;

export type StreamedWaveform =
    | { volumes: Float32Array }
    /** `hasAudioTrack` is null when the file couldn't be inspected at all. */
    | { volumes: null; hasAudioTrack: boolean | null };

/**
 * Decodes the source's audio a chunk at a time and folds each chunk into the waveform as it
 * arrives, so memory stays flat however long the file is. Returns no volumes when the browser
 * can't decode the audio this way, so the caller can fall back to decoding it in one go.
 */
export async function streamWaveformVolumes(
    source: string,
    signal: AbortSignal,
): Promise<StreamedWaveform> {
    if (!('AudioDecoder' in globalThis)) {
        return { volumes: null, hasAudioTrack: null };
    }
    let input: Input | undefined;
    try {
        // Loaded on demand: the trimmer is the only part of the editor that needs it. Inside the
        // try so a chunk that fails to load (e.g. a tab open across a deploy) still falls back.
        const { ALL_FORMATS, AudioSampleSink, Input, UrlSource } = await import('mediabunny');
        input = new Input({
            source: new UrlSource(source, {
                getRetryDelay: (previousAttempts) =>
                    previousAttempts < MAX_FETCH_ATTEMPTS ? previousAttempts : null,
            }),
            formats: ALL_FORMATS,
        });
        const track = await input.getPrimaryAudioTrack();
        if (!track) {
            return { volumes: new Float32Array() };
        }
        if (!(await track.canDecode())) {
            return { volumes: null, hasAudioTrack: true };
        }
        const firstTimestamp = await track.getFirstTimestamp();
        const duration = (await track.computeDuration()) - firstTimestamp;
        if (duration <= 0) {
            return { volumes: new Float32Array() };
        }

        let numPoints = 0;
        let totals = new Float64Array();
        let frameCounts = new Float64Array();
        let frames = new Float32Array();
        for await (const sample of new AudioSampleSink(track).samples()) {
            try {
                if (signal.aborted) {
                    return { volumes: null, hasAudioTrack: true };
                }
                if (!numPoints) {
                    numPoints = Math.max(
                        1,
                        Math.min(MAX_WAVEFORM_POINTS, Math.floor(duration * sample.sampleRate)),
                    );
                    totals = new Float64Array(numPoints);
                    frameCounts = new Float64Array(numPoints);
                }
                if (frames.length < sample.numberOfFrames) {
                    frames = new Float32Array(sample.numberOfFrames);
                }
                // Only the first channel is needed to draw the waveform
                sample.copyTo(frames, { planeIndex: 0, format: 'f32-planar' });

                const framesPerPoint = (duration * sample.sampleRate) / numPoints;
                const firstFrame = (sample.timestamp - firstTimestamp) * sample.sampleRate;
                for (let i = 0; i < sample.numberOfFrames; i++) {
                    const point = Math.min(
                        numPoints - 1,
                        Math.max(0, Math.floor((firstFrame + i) / framesPerPoint)),
                    );
                    const val = frames[i];
                    totals[point] += val < 0 ? -val : val;
                    frameCounts[point] += 1;
                }
            } finally {
                sample.close();
            }
        }
        return {
            volumes: Float32Array.from(totals, (total, i) =>
                frameCounts[i] ? total / frameCounts[i] : 0,
            ),
        };
    } catch {
        return { volumes: null, hasAudioTrack: null };
    } finally {
        input?.dispose();
    }
}
